import { useState } from "react";
import { CARD } from "../lib/planner";
import { ListChecks, RefreshCw, ChevronDown, Clock, Timer, MoveRight, Check, AlertTriangle, Target, Loader2, Moon } from "lucide-react";
import { toast } from "sonner";

const setFocusTask = (task) => {
  localStorage.setItem("focusTask", JSON.stringify({ id: task.id, content: task.content }));
  window.dispatchEvent(new Event("focus-task-changed"));
  toast.success(`Linked "${task.content.slice(0, 30)}" to your timer`);
};

const TimeBadge = ({ t }) => {
  if (t.due_time)
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-stone-500 dark:text-stone-400">
        <Clock className="h-3 w-3" /> {t.due_time}
      </span>
    );
  return <span className="text-[11px] font-medium text-stone-400 dark:text-stone-500">{t.suggested_slot}</span>;
};

const TaskRow = ({ t, bucket, onComplete, onReopen, onPostpone, showFocus }) => {
  const done = t.completed;
  return (
    <div
      data-testid={`task-row-${t.id}`}
      className="flex items-center justify-between gap-3 py-3 border-b border-stone-100 dark:border-stone-800 last:border-0"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          data-testid={done ? `reopen-task-${t.id}` : `complete-task-${t.id}`}
          onClick={() => (done ? onReopen(t, bucket) : onComplete(t, bucket))}
          title={done ? "Undo — mark as not done" : "Mark complete"}
          className={`group h-6 w-6 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
            done
              ? "bg-[#b1543b] border-[#b1543b] dark:bg-stone-100 dark:border-stone-100"
              : "border-stone-300 dark:border-stone-600 hover:border-[#b1543b] hover:bg-[#b1543b]"
          }`}
        >
          <Check className={`h-3.5 w-3.5 ${done ? "text-white dark:text-stone-900" : "text-transparent group-hover:text-white"}`} />
        </button>
        <div className="min-w-0">
          <p
            className={`text-base truncate ${
              done
                ? "text-stone-400 dark:text-stone-500 line-through"
                : t.good_for_today
                ? "text-[#3b2a24] dark:text-stone-100"
                : "text-stone-400 dark:text-stone-500 line-through"
            }`}
          >
            {t.content}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-stone-400 dark:text-stone-500">{t.project}</span>
            {!done && <TimeBadge t={t} />}
            {!done && t.conflict && (
              <span
                data-testid={`conflict-${t.id}`}
                title={`Overlaps with "${t.conflict.summary}"`}
                className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-950/50 px-2 py-0.5 rounded-full"
              >
                <AlertTriangle className="h-3 w-3" /> Clashes with {t.conflict.summary}
              </span>
            )}
          </div>
        </div>
      </div>
      {!done && (
        <div className="flex items-center gap-1 shrink-0">
          {showFocus && (
            <button
              data-testid={`focus-task-${t.id}`}
              onClick={() => setFocusTask(t)}
              title="Focus with Pomodoro"
              className="p-2 rounded-full text-stone-400 hover:text-[#3b2a24] dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800"
            >
              <Timer className="h-4 w-4" />
            </button>
          )}
          <button
            data-testid={`postpone-task-${t.id}`}
            onClick={() => onPostpone(t)}
            title="Postpone to tomorrow"
            className="p-2 rounded-full text-stone-400 hover:text-amber-600 hover:bg-stone-100 dark:hover:bg-stone-800"
          >
            <MoveRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export const TaskList = ({ data, loading, label, onComplete, onReopen, onPostpone, onRefresh, onOpenFocus }) => {
  const [showMore, setShowMore] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const top3 = data?.top3 || [];
  const more = data?.fits_today || [];
  const skip = data?.maybe_skip || [];
  const hasOpen = [...top3, ...more].some((t) => !t.completed);
  const isRest = label === "Rest";

  if (isRest && !revealed) {
    return (
      <div data-testid="task-list-rest-card" className={`${CARD} p-8 sm:p-10 text-center`}>
        <div className="mx-auto h-12 w-12 rounded-full bg-[#e8c8b9] dark:bg-[#3a241c] flex items-center justify-center mb-4">
          <Moon className="h-6 w-6 text-[#74331f] dark:text-[#eab79f]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-[#3b2a24] dark:text-stone-100">Rest is the task today</h2>
        <p className="mt-3 text-base text-stone-600 dark:text-stone-300 max-w-sm mx-auto leading-relaxed">
          Your to-do list is resting too. On low-capacity days, doing less <span className="italic">is</span> doing enough — let your body lead. Your essentials below are all that's asked of you.
        </p>
        <button
          data-testid="reveal-tasks-btn"
          onClick={() => setRevealed(true)}
          className="mt-6 text-sm font-semibold text-[#b1543b] hover:underline underline-offset-4"
        >
          Peek at my tasks anyway
        </button>
      </div>
    );
  }

  return (
    <div data-testid="task-list-card" className={`${CARD} p-6 sm:p-8`}>
      {isRest && revealed && (
        <button
          data-testid="hide-tasks-btn"
          onClick={() => setRevealed(false)}
          className="mb-4 flex items-center gap-1.5 text-xs font-semibold text-stone-400 hover:text-[#b1543b]"
        >
          <Moon className="h-3.5 w-3.5" /> Only if you're up for it — tap to head back to rest
        </button>
      )}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-stone-400" />
          <h2 className="font-heading text-2xl font-bold text-[#3b2a24] dark:text-stone-100">Top 3 for today</h2>
        </div>
        <button
          data-testid="refresh-tasks-btn"
          onClick={onRefresh}
          className="p-2 rounded-full text-stone-400 hover:text-[#3b2a24] dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800"
          title="Refresh from Todoist"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {data?.mock && (
        <p className="text-xs text-stone-400 mb-3">Sample tasks — add your Todoist token to see your list.</p>
      )}

      {loading && !data ? (
        <div className="flex items-center gap-2 text-stone-400 py-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Sorting to fit your day…
        </div>
      ) : (
        <div className="mt-3">
          {top3.length ? (
            top3.map((t) => (
              <TaskRow key={t.id} t={t} bucket="top3" onComplete={onComplete} onReopen={onReopen} onPostpone={onPostpone} showFocus />
            ))
          ) : (
            <p className="text-sm text-stone-400 py-2">Nothing pressing — enjoy the space.</p>
          )}

          {hasOpen && (
            <button
              data-testid="protect-focus-btn"
              onClick={onOpenFocus}
              className="mt-4 w-full flex items-center justify-center gap-2 h-11 rounded-full border border-[#b1543b]/30 bg-[#e7d2c4] dark:bg-stone-800 text-[#b1543b] dark:text-stone-200 text-sm font-semibold hover:-translate-y-0.5 transition-transform duration-200 active:scale-[0.98]"
            >
              <Target className="h-4 w-4" /> Protect time for #1
            </button>
          )}

          {more.length > 0 && (
            <div className="mt-4">
              <button
                data-testid="toggle-more-fits"
                onClick={() => setShowMore((v) => !v)}
                className="flex items-center gap-2 text-sm font-semibold text-stone-500 dark:text-stone-400 hover:text-[#3b2a24] dark:hover:text-stone-100"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showMore ? "rotate-180" : ""}`} />
                {showMore ? "Hide" : `Show ${more.length} more that fit today`}
              </button>
              {showMore && (
                <div className="mt-2">
                  {more.map((t) => (
                    <TaskRow key={t.id} t={t} bucket="fits" onComplete={onComplete} onReopen={onReopen} onPostpone={onPostpone} showFocus />
                  ))}
                </div>
              )}
            </div>
          )}

          {skip.length > 0 && (
            <div className="mt-4">
              <button
                data-testid="toggle-skip"
                onClick={() => setShowSkip((v) => !v)}
                className="flex items-center gap-2 text-sm font-semibold text-stone-400 dark:text-stone-500 hover:text-stone-600"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showSkip ? "rotate-180" : ""}`} />
                {showSkip ? "Hide" : `Maybe skip today (${skip.length})`}
              </button>
              {showSkip && (
                <div className="mt-2">
                  {skip.map((t) => (
                    <TaskRow key={t.id} t={t} bucket="skip" onComplete={onComplete} onReopen={onReopen} onPostpone={onPostpone} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
